// lib/leasing/newlyAddedLeads.ts
//
// WHICH ROWS THE EXTENSION NOW RUNNING HAS ADDED.
//
// Extending a shortlist adds brands to a list the reader already knows, and nothing distinguished
// them while they landed: two of three arrivals were dismissed on the run that prompted this, so
// three billed brands looked like one, and "which brands have been added" had to be asked twice.
//
// THE BADGE MARKS THE LATEST BATCH, AND A NEWER ONE REPLACES IT.
//
// It was first tied to the run being live, exactly as asked, and that turned out to be invisible:
// a pass takes minutes and people read the list afterwards. Measured 2026-08-16 - the row landed at
// 21:01, the run completed at 21:25, the list was read at 22:27, and by then the badge was
// correctly gone and had never been seen. So it now survives the run and clears when the NEXT
// extension arrives, which is self-clearing and needs no clock.
//
// The outer bound is a week: a shortlist nobody has extended since spring should not still be
// announcing its last arrivals as new.
//
// DERIVED, NOT STORED. Every lead already carries `createdAt`, and an extension writes its rows
// within seconds of each other, so the newest cluster IS the batch arriving. No column, no
// migration, no write path.

/** How far apart two rows can be and still count as the same arrival. */
const SAME_BATCH_MS = 5 * 60 * 1000;

/** Past this, the last arrivals stop being news. A newer extension clears them long before. */
const STAYS_NEW_MS = 7 * 24 * 60 * 60 * 1000;

const timeOf = (value: string | Date | null | undefined): number => {
  if (!value) return NaN;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : NaN;
};

/** The ids of the rows the most recent extension added, or an empty set. */
export function newlyAddedLeadIds(
  leads: ReadonlyArray<{ id?: string | null; createdAt?: string | Date | null }>,
  now: number = Date.now(),
): Set<string> {
  const dated = leads
    .map((lead) => ({ id: typeof lead?.id === 'string' ? lead.id : '', at: timeOf(lead?.createdAt) }))
    .filter((entry) => entry.id.length > 0 && Number.isFinite(entry.at));
  if (dated.length === 0) return new Set();

  const newest = Math.max(...dated.map((entry) => entry.at));
  if (now - newest > STAYS_NEW_MS) return new Set();

  // A FIRST RUN IS NOT AN EXTENSION. When every row on the list arrived together, the whole
  // shortlist is that batch, and badging all of it says nothing: the reader knows, they just
  // launched it. Only a batch that is a genuine subset marks anything.
  const batch = dated.filter((entry) => newest - entry.at <= SAME_BATCH_MS);
  if (batch.length === dated.length) return new Set();

  return new Set(batch.map((entry) => entry.id));
}
